import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";
import { useCurrentLocation } from "./useCurrentLocation";

export interface IParcel {
  _id?: string;
  courier: string;
  location: string;
  date: Date;
  volume: number;
  revenue: number;
  createdAt?: string;
  updatedAt?: string;
}
interface ITrending {
  name: string;
  data: number[];
}

interface MonthlyData {
  totalVolume: number;
  totalRevenue: number;
  parcelsCount: number;
}

interface CourierMonthlyData {
  courier: {
    _id: string;
    name: string;
    // add other courier properties you need
  };
  months: {
    [key: number]: MonthlyData;
  };
}

interface IStore {
  /** State */
  parcelsVolume: ITrending[];
  parcelsRevenue: ITrending[];
  parcelsConversion: ITrending[];
  parcelItemsPerLocation: ITrending[];
  parcelRevenuePerLocation: ITrending[];
  averagePricePerParcelPerLocation: ITrending[];
  averagePricePerParcelPerCourier: ITrending[];
  parcelVolumesPerCourier: ITrending[];
  parcelVolumesPerCourierPerLocation: {
    locationLabels: string[];
    courierSeries: { name: string; data: number[] }[];
  } | null;
  parcelRevenuePerCourierPerLocation: {
    locationLabels: string[];
    courierSeries: { name: string; data: number[] }[];
  } | null;
  isLoadingParcelRevenuePerLocation: boolean;
  isParcelRevenuePerLocationError: boolean;
  isLoadingAveragePricePerParcel: boolean;
  isAveragePricePerParcelError: boolean;
  allParcels: IParcel[];
  allGroupedParcels: CourierMonthlyData[];
  hasNextPage: boolean;
  oneParcel: IParcel | null;
  addedParcel?: string | null;
  updatedParcel?: string | null;
  /** Callbacks */
  getParcelsVolume: (
    requestedDay?: Date,
    period?: "monthly" | "yearly"
  ) => void;
  getParcelsRevenue: (
    requestedDay?: Date,
    period?: "monthly" | "yearly"
  ) => void;
  getParcelsConversion: (
    requestedDay?: Date,
    period?: "monthly" | "yearly"
  ) => void;
  getParcelItemsPerLocation: (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    type?: "location" | "city",
    places?: string[],
    courierId?: string
  ) => void;
  getParcelRevenuePerLocation: (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    type?: "location" | "city",
    places?: string[],
    courierId?: string
  ) => void;
  getAveragePricePerParcelPerLocation: (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    type?: "location" | "city",
    places?: string[]
  ) => void;
  getAveragePricePerParcelPerCourier: (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number
  ) => void;
  getParcelVolumesPerCourier: (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    type?: "location" | "city",
    places?: string[]
  ) => void;
  getParcelVolumesPerCourierPerLocation: (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    type?: "location" | "city",
    places?: string[]
  ) => void;
  getParcelRevenuePerCourierPerLocation: (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    type?: "location" | "city",
    places?: string[]
  ) => void;
  getAllParcels: (courierId: string, page: number, limit: number) => void;
  getAllGroupedParcelsByCourier: (requestedDate: Date) => void;
  getOneParcel: (parcelId: string) => void;
  addParcel: (data: IParcel) => void;
  deleteParcel: (parcelId: string) => void;
  updateParcel: (parcelId: string, data: IParcel) => void;
  clearAddedParcel: () => void;
  clearUpdatedParcel: () => void;
}

export const useParcels = create<IStore>()((set) => ({
  /** State */
  allGroupedParcels: [],
  parcelsVolume: [],
  parcelsRevenue: [],
  parcelsConversion: [],
  parcelItemsPerLocation: [],
  parcelRevenuePerLocation: [],
  averagePricePerParcelPerLocation: [],
  averagePricePerParcelPerCourier: [],
  parcelVolumesPerCourier: [],
  parcelVolumesPerCourierPerLocation: null,
  parcelRevenuePerCourierPerLocation: null,
    isLoadingParcelRevenuePerLocation: false,
    isParcelRevenuePerLocationError: false,
    isLoadingAveragePricePerParcel: false,
    isAveragePricePerParcelError: false,
  allParcels: [],
  hasNextPage: false,
  oneParcel: null,
  addedParcel: null,
  updatedParcel: null,
  /** Callbacks */
  getParcelsVolume: async (requestedDay = new Date(), period = "monthly") => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `/parcels/stats/parcels-volume?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
      );
      if (response?.data) {
        set({ parcelsVolume: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Parcels Volume stats", {
        error,
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getParcelsRevenue: async (requestedDay = new Date(), period = "monthly") => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `/parcels/stats/parcels-revenue?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
      );
      if (response?.data) {
        set({ parcelsRevenue: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Parcels Revenue stats", {
        error,
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getParcelsConversion: async (
    requestedDay = new Date(),
    period = "monthly"
  ) => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `/parcels/stats/parcels-conversion?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
      );
      if (response?.data) {
        set({ parcelsConversion: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Parcels Conversion stats", {
        error,
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getParcelItemsPerLocation: async (
    startYear,
    startMonth,
    endYear,
    endMonth,
    type?: "location" | "city",
    places?: string[],
    courierId?: string
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        startYear: startYear.toString(),
        startMonth: startMonth.toString(),
        endYear: endYear.toString(),
        endMonth: endMonth.toString(),
      });
      if (type) queryParams.append("type", type);
      if (places?.length) queryParams.append("places", places.join(","));
      if (courierId) queryParams.append("courierId", courierId);
      const response = await axios.get(
        `/parcels/stats/parcel-items-per-location?${queryParams.toString()}`
      );
      if (response?.data) {
        set({ parcelItemsPerLocation: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Parcel Items Per Location stats", {
        error,
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getParcelRevenuePerLocation: async (
    startYear,
    startMonth,
    endYear,
    endMonth,
    type?: "location" | "city",
    places?: string[],
    courierId?: string
  ) => {
    set({ isLoadingParcelRevenuePerLocation: true, isParcelRevenuePerLocationError: false });
    try {
      const queryParams = new URLSearchParams({
        startYear: startYear.toString(),
        startMonth: startMonth.toString(),
        endYear: endYear.toString(),
        endMonth: endMonth.toString(),
      });
      if (type) queryParams.append("type", type);
      if (places?.length) queryParams.append("places", places.join(","));
      if (courierId) queryParams.append("courierId", courierId);
      const response = await axios.get(
        `/parcels/stats/parcel-revenue-per-location?${queryParams.toString()}`
      );
      if (response?.data) {
        set({ parcelRevenuePerLocation: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Parcel Revenue Per Location stats", {
        error,
      });
      set({ isParcelRevenuePerLocationError: true });
    } finally {
      set({ isLoadingParcelRevenuePerLocation: false });
    }
  },
  getAveragePricePerParcelPerLocation: async (
    startYear,
    startMonth,
    endYear,
    endMonth,
    type?: "location" | "city",
    places?: string[]
  ) => {
    set({ isLoadingAveragePricePerParcel: true, isAveragePricePerParcelError: false });
    try {
      const queryParams = new URLSearchParams({
        startYear: startYear.toString(),
        startMonth: startMonth.toString(),
        endYear: endYear.toString(),
        endMonth: endMonth.toString(),
      });
      if (type) queryParams.append("type", type);
      if (places?.length) queryParams.append("places", places.join(","));
      const response = await axios.get(
        `/parcels/stats/average-price-per-parcel?${queryParams.toString()}`
      );
      if (response?.data) {
        set({ averagePricePerParcelPerLocation: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Average Price Per Parcel stats", { error });
      set({ isAveragePricePerParcelError: true });
    } finally {
      set({ isLoadingAveragePricePerParcel: false });
    }
  },
  getAveragePricePerParcelPerCourier: async (
    startYear,
    startMonth,
    endYear,
    endMonth
  ) => {
    set({ isLoadingAveragePricePerParcel: true, isAveragePricePerParcelError: false });
    try {
      const queryParams = new URLSearchParams({
        startYear: startYear.toString(),
        startMonth: startMonth.toString(),
        endYear: endYear.toString(),
        endMonth: endMonth.toString(),
      });
      const response = await axios.get(
        `/parcels/stats/average-price-per-parcel-per-courier?${queryParams.toString()}`
      );
      if (response?.data) {
        set({ averagePricePerParcelPerCourier: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Average Price Per Parcel Per Courier stats", { error });
      set({ isAveragePricePerParcelError: true });
    } finally {
      set({ isLoadingAveragePricePerParcel: false });
    }
  },
  getParcelVolumesPerCourier: async (
    startYear,
    startMonth,
    endYear,
    endMonth,
    type?: "location" | "city",
    places?: string[]
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        startYear: startYear.toString(),
        startMonth: startMonth.toString(),
        endYear: endYear.toString(),
        endMonth: endMonth.toString(),
      });
      if (type) queryParams.append("type", type);
      if (places?.length) queryParams.append("places", places.join(","));
      const response = await axios.get(
        `/parcels/stats/parcel-volumes-per-courier?${queryParams.toString()}`
      );
      if (response?.data) {
        set({ parcelVolumesPerCourier: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Parcel Volumes Per Courier stats", {
        error,
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getParcelVolumesPerCourierPerLocation: async (
    startYear,
    startMonth,
    endYear,
    endMonth,
    type?: "location" | "city",
    places?: string[]
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        startYear: startYear.toString(),
        startMonth: startMonth.toString(),
        endYear: endYear.toString(),
        endMonth: endMonth.toString(),
      });
      if (type) queryParams.append("type", type);
      if (places?.length) queryParams.append("places", places.join(","));
      const response = await axios.get(
        `/parcels/stats/parcel-volumes-per-courier-per-location?${queryParams.toString()}`
      );
      if (response?.data) {
        set({ parcelVolumesPerCourierPerLocation: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Parcel Volumes Per Courier Per Location", {
        error,
      });
      set({ parcelVolumesPerCourierPerLocation: null });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getParcelRevenuePerCourierPerLocation: async (
    startYear,
    startMonth,
    endYear,
    endMonth,
    type?: "location" | "city",
    places?: string[]
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        startYear: startYear.toString(),
        startMonth: startMonth.toString(),
        endYear: endYear.toString(),
        endMonth: endMonth.toString(),
      });
      if (type) queryParams.append("type", type);
      if (places?.length) queryParams.append("places", places.join(","));
      const response = await axios.get(
        `/parcels/stats/parcel-revenue-per-courier-per-location?${queryParams.toString()}`
      );
      if (response?.data) {
        set({ parcelRevenuePerCourierPerLocation: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Parcel Revenue Per Courier Per Location", {
        error,
      });
      set({ parcelRevenuePerCourierPerLocation: null });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getAllParcels: async (courierId, page = 1, limit = 10) => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `parcels?courierId=${courierId}&page=${page}&limit=${limit}&location=${currentLocation}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextPage: response?.data?.length === limit,
          allParcels:
            page === 1
              ? response?.data
              : [...state.allParcels, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching all Parcels", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getAllGroupedParcelsByCourier: async (requestedDate) => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `parcels/grouped-by-courier?requestedDate=${requestedDate}&location=${currentLocation}`
      );
      if (response?.data) {
        set({ allGroupedParcels: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching all Grouped Parcels by Courier", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOneParcel: async (parcelId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`parcels/${parcelId}`);
      if (response?.data) {
        set({ oneParcel: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching one Parcel", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addParcel: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("parcels", body);
      if (response?.data) {
        const newParcel = response.data;
        set((state) => ({
          allParcels: [newParcel, ...state.allParcels],
          addedParcel: newParcel._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding new Parcel", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteParcel: async (parcelId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`parcels/${parcelId}`);
      set((state) => ({
        allParcels: state.allParcels.filter(
          (parcel) => parcel._id !== parcelId
        ),
      }));
    } catch (error) {
      LOG.error("Error on deleting a Parcel", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateParcel: async (parcelId, body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`parcels/${parcelId}`, body);
      if (response?.data) {
        const updatedParcel = response.data;
        set((state) => ({
          allParcels: state.allParcels.map((parcel) => {
            if (parcel._id === updatedParcel._id) {
              return updatedParcel;
            }
            return parcel;
          }),
          updatedParcel: updatedParcel._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating a Parcel", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedParcel: () => {
    set({ addedParcel: null });
  },
  clearUpdatedParcel: () => {
    set({ updatedParcel: null });
  },
}));
